import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Users, Trophy, Timer, ArrowRight } from "lucide-react";

export default function AdminMatches() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState("");
  const [matches, setMatches] = useState([]);
  const [context, setContext] = useState(null);
  const [form, setForm] = useState({ date: "", kickoff_time: "", opponent: "", competition: "", home_away: "home" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => {}); }, []);

  const load = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const [m, ctx] = await Promise.all([
        api.get(`/admin/matches?client_id=${selected}`),
        api.get(`/admin/matches/context/${selected}?days_ahead=14`),
      ]);
      setMatches(m.data);
      setContext(ctx.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [selected]);

  const create = async () => {
    if (!selected || !form.date) return toast.error("Cliente y fecha requeridos");
    try {
      await api.post("/admin/matches", { client_id: selected, ...form });
      toast.success("Partido añadido");
      setForm({ date: "", kickoff_time: "", opponent: "", competition: "", home_away: "home" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminar partido?")) return;
    try { await api.delete(`/admin/matches/${id}`); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const labelColor = (l) => {
    if (!l) return "#525252";
    if (l === "MD") return "#dc2626";
    if (l === "MD-1") return "#facc15";
    if (l === "MD+1") return "#60a5fa";
    return "#a3a3a3";
  };

  return (
    <AppShell>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>Motor de Partidos</h1>
        <p style={{ color: "#a3a3a3", fontSize: 13, margin: "4px 0 0" }}>Contexto MD/MD-1/MD+1 para periodizar. Sólo sugiere: no modifica sesiones automáticamente.</p>
      </div>

      <select value={selected} onChange={(e) => setSelected(e.target.value)} style={inputStyle} data-testid="matches-client">
        <option value="">— Selecciona cliente —</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      {selected && (
        <>
          <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", padding: 16, borderRadius: 4, marginTop: 20 }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>Nuevo partido</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={inputStyle} data-testid="match-date" />
              <input type="time" value={form.kickoff_time} onChange={(e) => setForm({ ...form, kickoff_time: e.target.value })} placeholder="Kickoff" style={inputStyle} />
              <input value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} placeholder="Rival" style={inputStyle} data-testid="match-opponent" />
              <input value={form.competition} onChange={(e) => setForm({ ...form, competition: e.target.value })} placeholder="Competición" style={inputStyle} />
              <select value={form.home_away} onChange={(e) => setForm({ ...form, home_away: e.target.value })} style={inputStyle}>
                <option value="home">Local</option>
                <option value="away">Visitante</option>
              </select>
              <button onClick={create} style={btnPrimary} data-testid="match-create"><Plus size={14}/> Añadir</button>
            </div>
          </div>

          {context && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 10 }}>Contexto próximos 14 días</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 6 }}>
                {context.days.map((d) => (
                  <div key={d.date} style={{ background: "#0a0a0a", border: `1px solid ${labelColor(d.label)}44`, padding: 8, borderRadius: 3, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#a3a3a3" }}>{d.date.slice(5)}</div>
                    <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 18, color: labelColor(d.label), letterSpacing: "0.08em", marginTop: 2 }}>{d.label || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 10 }}>Partidos programados</div>
            <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }}>
              {loading && <div style={{ padding: 20, color: "#a3a3a3", textAlign: "center" }}>Cargando…</div>}
              {!loading && matches.length === 0 && <div style={{ padding: 20, color: "#a3a3a3", textAlign: "center" }}>Sin partidos programados.</div>}
              {matches.map((m) => (
                <div key={m.id} style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{m.opponent || "Sin rival"} <span style={{ color: "#a3a3a3", fontSize: 12, marginLeft: 6 }}>{m.competition || ""}</span></div>
                    <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 2 }}>{m.date} {m.kickoff_time || ""} · {m.home_away === "away" ? "Visitante" : "Local"}</div>
                  </div>
                  <button onClick={() => del(m.id)} style={{ background: "transparent", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171", padding: "6px 12px", fontSize: 12, cursor: "pointer", borderRadius: 2 }}>Eliminar</button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}

const inputStyle = { background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "9px 12px", fontSize: 14, borderRadius: 3, outline: "none", boxSizing: "border-box", width: "100%" };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: 6, background: "#dc2626", color: "#fff", border: "none", padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", borderRadius: 2 };
